import * as React from 'react';
import './Billing.scss';
import { TK } from '../../store/translations/translationKeys';
import { Translate, getLanguage, useTranslations } from '../../store/translations/hooks';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import moment from 'moment';
import { FeatureEnum, InvoiceReduced, InvoiceDocumentType, InvoiceState, InvoiceSummaryData } from '../../models';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminInvoicesAsync, getAdminInvoicesSummaryAsync, downloadInvoicesSaftFileAsync, exportInvoicePdfsZipAsync } from '../../ajax/requests';
import { getErrorMessage } from '../../ajax/errors';
import Typography from '../../components/Typography';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { AutocompleteTextField, FormField, SelectTextField } from '../../components/Forms/fields';
import { alertGenericError, alertSuccess, alertError } from '../../store/alerts/actions';
import { InvoicesTable } from '../Clients/ClientInvoicesTable';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ClientEntity } from '../../store/entities/state';
import { getGeneralSettings } from '../../store/settings/selectors';
import { getSelectedBranch } from '../../store/general/selectors';
import { hasFeature, canManagePrices } from '../../store/session/selectors';
import {
    getInvoiceBranchFilterOptions,
    resolveInvoiceBranchFilterId,
} from '../../shared/appointmint/invoiceBranches';
import {
    createClientSearchConfig,
} from '../../shared/clientSearch';
import AppButton from '../../components/AppButton';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { formatEuros } from '../../shared/string';
import HelpIcon from '@material-ui/icons/Help';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';

const renderHelpText = (text: string) =>
    text.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br/></React.Fragment>);

const SummaryCardHelp: React.FC<{ label: string; helpText: string }> = ({ label, helpText }) => {
    const [open, setOpen] = React.useState(false)
    return (
        <>
            <IconButton size="small" onClick={() => setOpen(true)} style={{ padding: 0, marginLeft: '4px', verticalAlign: 'middle' }}>
                <HelpIcon style={{ fontSize: '14px', color: 'var(--text-muted)' }} />
            </IconButton>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>{label}</DialogTitle>
                <DialogContent>{renderHelpText(helpText)}</DialogContent>
            </Dialog>
        </>
    )
}

const invoiceTypeOptions: InvoiceDocumentType[] = [
    InvoiceDocumentType.Invoice,
    InvoiceDocumentType.SimplifiedInvoice,
    InvoiceDocumentType.InvoiceReceipt,
    InvoiceDocumentType.CreditNote,
    InvoiceDocumentType.Receipt,
]

const invoiceStateOptions: InvoiceState[] = [
    InvoiceState.Draft,
    InvoiceState.Issued,
    InvoiceState.Canceled,
]

const invoiceTypeLabel = (type: InvoiceDocumentType, t: Translate<TK>): string => {
    switch (type) {
        case InvoiceDocumentType.Invoice: return t(TK.invoice)
        case InvoiceDocumentType.SimplifiedInvoice: return t(TK.simplifiedInvoice)
        case InvoiceDocumentType.InvoiceReceipt: return t(TK.invoiceReceipt)
        case InvoiceDocumentType.CreditNote: return t(TK.creditNote)
        case InvoiceDocumentType.Receipt: return t(TK.receipt)
        default: return InvoiceDocumentType[type]
    }
}

const PAGE_SIZE = 20;


const Invoices: React.FC = () => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const [invoices, setInvoices] = React.useState<InvoiceReduced[]>([])
    const [isInitialLoading, setIsInitialLoading] = React.useState(false)
    const [isLoadingMore, setIsLoadingMore] = React.useState(false)
    const [hasMoreInvoices, setHasMoreInvoices] = React.useState(false)
    const [nextPage, setNextPage] = React.useState(1)
    const [summary, setSummary] = React.useState<InvoiceSummaryData | null>(null)
    const [isSummaryLoading, setIsSummaryLoading] = React.useState(false)
    const loadMoreObserver = React.useRef<HTMLDivElement | null>(null)
    const fetchCountRef = React.useRef(0)
    const queryKeyRef = React.useRef<string>('')

    const [dates, setDates] = React.useState<DateRangeSelectorState>({
        from: moment().startOf('month').format('YYYY-MM-DD'),
        to: moment().endOf('month').format('YYYY-MM-DD'),
        type: 'month',
    })

    const [statusFilter, setStatusFilter] = React.useState<InvoiceState | ''>('')
    const [typeFilter, setTypeFilter] = React.useState<InvoiceDocumentType | ''>('')
    const [withoutReceiptFilter, setWithoutReceiptFilter] = React.useState(false)
    const generalSettings = useSelector(getGeneralSettings)
    const useBranches = useSelector(hasFeature(FeatureEnum.Branches))
    const hasPriviledgedAccess = useSelector(canManagePrices)
    const selectedBranch = useSelector(getSelectedBranch)
    const [branchFilterId, setBranchFilterId] = React.useState<string>('')
    const [clientFilterValue, setClientFilterValue] = React.useState<string>('')
    const clientFilterOptions = useSelector(getEntitiesDataItems<ClientEntity>('clients'))
    const [showCreateInvoiceModal, setShowCreateInvoiceModal] = React.useState(false)
    const [showExportSaftModal, setShowExportSaftModal] = React.useState(false)
    const [saftYear, setSaftYear] = React.useState<string>(moment().format('YYYY'))
    const [saftMonth, setSaftMonth] = React.useState<string>(moment().format('MM'))
    const [isExportingSaft, setIsExportingSaft] = React.useState(false)
    const [isExportingInvoicePdfs, setIsExportingInvoicePdfs] = React.useState(false)
    const clientFilterSearchConfig = React.useMemo(() => createClientSearchConfig(clientFilterOptions), [clientFilterOptions])
    const branchOptions = React.useMemo(
        () => getInvoiceBranchFilterOptions(generalSettings?.branches),
        [generalSettings?.branches]
    )
    const invoiceTypeOptionLabels = React.useMemo(
        () => [t(TK.all), ...invoiceTypeOptions.map(type => invoiceTypeLabel(type, t)), t(TK.invoicesWithoutReceipt)],
        [t]
    )
    const branchOptionLabels = React.useMemo(
        () => [t(TK.allBranches), ...branchOptions.map(branch => branch.label)],
        [branchOptions, t]
    )
    const branchFilterLabel = branchFilterId
        ? branchOptions.find(branch => branch.id === branchFilterId)?.label ?? branchFilterId
        : t(TK.allBranches)

    const clientFilterId = React.useMemo(() =>
        clientFilterSearchConfig.getClientByOption(clientFilterValue)?.id ?? '',
        [clientFilterSearchConfig, clientFilterValue]
    )
    const filtersKey = React.useMemo(() =>
        `${dates?.from ?? ''}|${dates?.to ?? ''}|${statusFilter ?? ''}|${typeFilter ?? ''}|${clientFilterId ?? ''}|${branchFilterId ?? ''}|${withoutReceiptFilter}`,
        [dates?.from, dates?.to, statusFilter, typeFilter, clientFilterId, branchFilterId, withoutReceiptFilter]
    )

    queryKeyRef.current = filtersKey

    React.useEffect(() => {
        if (!useBranches) {
            if (branchFilterId) {
                setBranchFilterId('')
            }
            return
        }

        if (!branchFilterId && selectedBranch && generalSettings?.branches?.[selectedBranch]) {
            setBranchFilterId(selectedBranch)
        }
    }, [branchFilterId, generalSettings?.branches, selectedBranch, useBranches])

    const handleDateRangeChanged = (window: DateRangeSelectorState) => {
        setDates(window)
    }

    const handleTypeSelection = (value: string) => {
        if (value === t(TK.invoicesWithoutReceipt)) {
            setTypeFilter('')
            setWithoutReceiptFilter(true)
            return
        }
        setWithoutReceiptFilter(false)
        if (value === '' || value === t(TK.all)) return setTypeFilter('')
        const parsed = invoiceTypeOptions.find(type => invoiceTypeLabel(type, t) === value)
        setTypeFilter(parsed ?? '')
    }

    const handleBranchSelection = (value: string) => {
        if (!value || value === t(TK.allBranches)) {
            setBranchFilterId('')
            return
        }

        const branchId = resolveInvoiceBranchFilterId(value, generalSettings?.branches) ?? ''
        setBranchFilterId(branchId)
    }

    const isLoading = (isInitialLoading && invoices.length === 0) || isLoadingMore

    const summaryChartData = React.useMemo(() => (summary?.daily ?? []).map(day => ({
        ...day,
        date: moment(day.date).locale(getLanguage()).format('DD MMM'),
    })), [summary?.daily])

    const loadInvoices = React.useCallback(async (pageToLoad: number, append: boolean) => {
        if (!dates) return

        fetchCountRef.current += 1
        if (append) setIsLoadingMore(true)
        else setIsInitialLoading(true)

        const currentQueryKey = filtersKey

        try {
            const result = await getAdminInvoicesAsync(dates.from, dates.to, {
                status: statusFilter === '' ? undefined : statusFilter,
                type: typeFilter === '' ? undefined : typeFilter,
                clientId: clientFilterId || undefined,
                branchId: branchFilterId || undefined,
                withoutReceipt: withoutReceiptFilter || undefined,
                page: pageToLoad,
                pageSize: PAGE_SIZE,
            })

            if (queryKeyRef.current !== currentQueryKey) return

            setInvoices(prev => {
                const merged = append ? [...prev, ...result] : result;
                return merged.slice().sort((a, b) => b.date < a.date ? -1 : b.date > a.date ? 1 : 0);
            })
            setHasMoreInvoices(result.length === PAGE_SIZE)
            setNextPage(pageToLoad + 1)
        } catch (err) {
            const message = getErrorMessage(err)
            if (message) {
                dispatch(alertError(message))
            } else {
                dispatch(alertGenericError())
            }
        } finally {
            fetchCountRef.current = Math.max(0, fetchCountRef.current - 1)
            if (append) setIsLoadingMore(false)
            else setIsInitialLoading(false)
        }
    }, [dates, filtersKey, dispatch, statusFilter, typeFilter, clientFilterId, branchFilterId, withoutReceiptFilter])

    const loadSummary = React.useCallback(async () => {
        if (!dates) return

        setIsSummaryLoading(true)
        const currentQueryKey = filtersKey

        try {
            const result = await getAdminInvoicesSummaryAsync(dates.from, dates.to, {
                status: statusFilter === '' ? undefined : statusFilter,
                type: typeFilter === '' ? undefined : typeFilter,
                clientId: clientFilterId || undefined,
                branchId: branchFilterId || undefined,
            })

            if (queryKeyRef.current !== currentQueryKey) return
            setSummary(result)
        } catch (err) {
            const message = getErrorMessage(err)
            if (message) {
                dispatch(alertError(message))
            } else {
                dispatch(alertGenericError())
            }
        } finally {
            setIsSummaryLoading(false)
        }
    }, [dates, filtersKey, dispatch, statusFilter, typeFilter, clientFilterId, branchFilterId])

    React.useEffect(() => {
        if (!dates) return
        setHasMoreInvoices(false)
        setNextPage(1)
        setSummary(null)
        loadInvoices(1, false)
        if (hasPriviledgedAccess) loadSummary()
    }, [dates, loadInvoices, loadSummary, filtersKey, hasPriviledgedAccess])

    const handleLoadMore = React.useCallback(() => {
        if (!hasMoreInvoices || fetchCountRef.current > 0) return
        loadInvoices(nextPage, true)
    }, [hasMoreInvoices, loadInvoices, nextPage])

    React.useEffect(() => {
        if (!hasMoreInvoices || isInitialLoading || isLoadingMore) return
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                handleLoadMore()
            }
        }, { rootMargin: '200px' })
        const current = loadMoreObserver.current
        if (current) observer.observe(current)
        return () => observer.disconnect()
    }, [hasMoreInvoices, handleLoadMore, isInitialLoading, isLoadingMore])

    const handleCreateInvoice = () => {
        setShowCreateInvoiceModal(true)
    }

    const handleInvoiceCreated = (invoice: InvoiceReduced) => {
        setInvoices([invoice, ...invoices])
        setShowCreateInvoiceModal(false)
    }

    const handleInvoiceUpdated = (invoice: InvoiceReduced) => {
        // Reload the entire list from the API to ensure all data is fresh
        // This is especially important after credit note emission
        loadInvoices(1, false)
    }

    const handleCloseCreateModal = () => {
        setShowCreateInvoiceModal(false)
    }

    const handleOpenExportSaftModal = () => {
        setShowExportSaftModal(true)
    }

    const handleCloseExportSaftModal = () => {
        setShowExportSaftModal(false)
    }

    const handleExportSaft = async () => {
        setIsExportingSaft(true)
        try {
            const result = await downloadInvoicesSaftFileAsync(saftYear, saftMonth)
            if ('url' in result) {
                window.open(result.url, '_blank')
                dispatch(alertSuccess(t(TK.exportSaft)))
            } else if ('message' in result) {
                dispatch(alertError(result.message))
            }
        } catch (err: any) {
            const message = getErrorMessage(err)
            if (message) dispatch(alertError(message))
            else dispatch(alertGenericError())
        } finally {
            setIsExportingSaft(false)
            setShowExportSaftModal(false)
        }
    }

    const handleExportInvoicePdfs = async () => {
        if (!dates) return

        setIsExportingInvoicePdfs(true)
        try {
            await exportInvoicePdfsZipAsync(dates.from, dates.to, {
                status: statusFilter === '' ? undefined : statusFilter,
                type: typeFilter === '' ? undefined : typeFilter,
                clientId: clientFilterId || undefined,
                branchId: branchFilterId || undefined,
            })
            dispatch(alertSuccess(t(TK.invoicePdfsExported)))
        } catch (err: any) {
            const message = getErrorMessage(err)
            if (message) dispatch(alertError(message))
            else dispatch(alertGenericError())
        } finally {
            setIsExportingInvoicePdfs(false)
        }
    }

    return (
        <div className='billingContainer'>
            {isLoading && <Typography style={{position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white'}}>{t(TK.loading)}...</Typography>}

            <DateRangeSelector onRangeChanged={handleDateRangeChanged}/>

            <div className="billingFiltersPanel">
                <FormField style={{margin: 0}}>
                    <SelectTextField
                        id="invoice-type-filter"
                        label={t(TK.filter)}
                        options={invoiceTypeOptionLabels}
                        allowUndefined={false}
                        value={withoutReceiptFilter ? t(TK.invoicesWithoutReceipt) : typeFilter === '' ? t(TK.all) : invoiceTypeLabel(typeFilter, t)}
                        onValueChanged={handleTypeSelection}
                    />
                </FormField>
                <FormField style={{margin: 0}}>
                    <AutocompleteTextField
                        options={clientFilterSearchConfig.options}
                        optionLabelResolver={clientFilterSearchConfig.optionLabelResolver}
                        searchTextResolver={clientFilterSearchConfig.searchTextResolver}
                        searchMatcher={clientFilterSearchConfig.searchMatcher}
                        value={clientFilterValue}
                        label={t(TK.client)}
                        onValueChanged={setClientFilterValue}
                        onTextChanged={setClientFilterValue}
                        placeholder={t(TK.client)}
                    />
                </FormField>
                {useBranches && (
                    <FormField style={{margin: 0}}>
                        <SelectTextField
                            id="invoice-branch-filter"
                            label={t(TK.branch)}
                            options={branchOptionLabels}
                            allowUndefined={false}
                            value={branchFilterLabel}
                            onValueChanged={handleBranchSelection}
                        />
                    </FormField>
                )}
            </div>

            <div className="billTypePanel" style={{flexDirection: 'row'}}>
                <AppButton color="success" onClick={handleCreateInvoice}>{t(TK.emitStandaloneInvoice)}</AppButton>
                {hasPriviledgedAccess && <AppButton onClick={handleOpenExportSaftModal}>{t(TK.exportSaft)}</AppButton>}
                {hasPriviledgedAccess && (
                    <AppButton onClick={handleExportInvoicePdfs} disabled={isExportingInvoicePdfs}>
                        {isExportingInvoicePdfs && <Spinner size="sm" style={{marginRight: '8px'}} />}
                        {isExportingInvoicePdfs ? t(TK.loading) : t(TK.exportInvoicePdfs)}
                    </AppButton>
                )}
            </div>

            {hasPriviledgedAccess && <div className="billingSummarySection">
                <div className="billingListHeader">
                    <h5>{t(TK.invoiceSummary)}</h5>
                </div>
                {isSummaryLoading && summary === null ? (
                    <div className="billingSummaryLoading">
                        <Spinner />
                    </div>
                ) : summary !== null && (
                    <div className="billingSummaryGrid">
                        <div className="billingSummaryCard billingSummaryCardPrimary">
                            <Typography size="smaller">
                                {t(TK.netInvoiced)}
                                <SummaryCardHelp label={t(TK.netInvoiced)} helpText={t(TK.netInvoicedHelp)} />
                            </Typography>
                            <strong>{formatEuros(summary.netInvoiced)}</strong>
                        </div>
                        <div className="billingSummaryCard">
                            <Typography size="smaller">
                                {t(TK.grossInvoiced)}
                                <SummaryCardHelp label={t(TK.grossInvoiced)} helpText={t(TK.grossInvoicedHelp)} />
                            </Typography>
                            <strong>{formatEuros(summary.grossInvoiced)}</strong>
                        </div>
                        <div className="billingSummaryCard">
                            <Typography size="smaller">
                                {t(TK.outstandingAmount)}
                                <SummaryCardHelp label={t(TK.outstandingAmount)} helpText={t(TK.outstandingAmountHelp)} />
                            </Typography>
                            <strong>{formatEuros(summary.outstandingTotal)}</strong>
                        </div>
                        <div className="billingSummaryCard">
                            <Typography size="smaller">
                                {t(TK.totalInvoices)}
                                <SummaryCardHelp label={t(TK.totalInvoices)} helpText={t(TK.totalInvoicesHelp)} />
                            </Typography>
                            <strong>{summary.totalInvoices}</strong>
                        </div>
                        <div className="billingSummaryCard">
                            <Typography size="smaller">
                                {t(TK.invoiceReceipt)}
                                <SummaryCardHelp label={t(TK.invoiceReceipt)} helpText={t(TK.invoiceReceiptHelp)} />
                            </Typography>
                            <strong>{summary.totalInvoiceReceipts}</strong>
                        </div>
                        <div className="billingSummaryCard">
                            <Typography size="smaller">
                                {t(TK.totalCreditNotes)}
                                <SummaryCardHelp label={t(TK.totalCreditNotes)} helpText={t(TK.totalCreditNotesHelp)} />
                            </Typography>
                            <strong>{summary.totalCreditNotes}</strong>
                        </div>
                    </div>
                )}
                {summaryChartData.length > 0 && (
                    <div className="billingSummaryChart">
                        <Typography size="small"><b>{t(TK.documentsByDay)}</b></Typography>
                        <BarChart
                            style={{ width: '100%', height: '260px' }}
                            data={summaryChartData}
                            margin={{ top: 12, right: 0, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis allowDecimals={false} width="auto" />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="invoices" name={t(TK.totalInvoices)} stackId="documents" fill="#2ab7af" />
                            <Bar dataKey="creditNotes" name={t(TK.totalCreditNotes)} stackId="documents" fill="#d66a5c" />
                            <Bar dataKey="receipts" name={t(TK.totalReceipts)} stackId="documents" fill="#49b2e5" />
                        </BarChart>
                    </div>
                )}
            </div>}

            <div className="billingListSection">
                <div className="billingListHeader">
                    <h5>{t(TK.invoicesHistory)} ({invoices.length})</h5>
                    {isLoadingMore && <Typography size='smaller'>{t(TK.loading)}...</Typography>}
                </div>
                {invoices.length === 0 && !isInitialLoading ? (
                    <Typography size='small'>{t(TK.noInvoicesEmitted)}</Typography>
                ) : (
                    <>
                        <InvoicesTable
                            clientId=""
                            invoices={invoices}
                            loading={isInitialLoading}
                            showClientName={true}
                            hideCreateButton={true}
                            showNavigationButtons={true}
                            compactActionButtons={true}
                            hideEmailButton={true}
                            highlightPendingReceipt={true}
                            onUpdate={handleInvoiceUpdated}
                        />
                        <div className="billingListFooter" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            {hasMoreInvoices && (
                                <AppButton color="link" onClick={handleLoadMore}>
                                    {t(TK.loadMore)}
                                </AppButton>
                            )}
                            {(isLoadingMore || (hasMoreInvoices && invoices.length === 0)) && (
                                <Typography size='small'>{t(TK.loading)}...</Typography>
                            )}
                            {hasMoreInvoices && <div ref={loadMoreObserver} style={{height: 1}} />}
                        </div>
                    </>
                )}
            </div>

            {/* Modal for creating new invoice */}
            {showCreateInvoiceModal && (
                <InvoicesTable
                    clientId=""
                    invoices={[]}
                    createNew={true}
                    allowCreditNoteCreation={true}
                    onCreated={handleInvoiceCreated}
                    onUpdate={handleInvoiceUpdated}
                    onModalClose={handleCloseCreateModal}
                />
            )}

            {/* Modal for exporting SAFT */}
            <Modal isOpen={showExportSaftModal} toggle={handleCloseExportSaftModal}>
                <ModalHeader toggle={handleCloseExportSaftModal}>{t(TK.exportSaft)}</ModalHeader>
                <ModalBody>
                    <div style={{display: 'flex', gap: '10px'}}>
                        <div style={{flex: 1}}>
                            <label>{t(TK.year)}</label>
                            <select
                                className="form-control"
                                value={saftYear}
                                onChange={(e) => setSaftYear(e.target.value)}
                            >
                                {Array.from({ length: new Date().getFullYear() - 2000 + 1 }, (_, i) => 2000 + i).reverse().map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{flex: 1}}>
                            <label>{t(TK.month)}</label>
                            <select
                                className="form-control"
                                value={saftMonth}
                                onChange={(e) => setSaftMonth(e.target.value)}
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                    <option key={month} value={month.toString().padStart(2, '0')}>
                                        {month.toString().padStart(2, '0')}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={handleCloseExportSaftModal}>{t(TK.cancel)}</AppButton>
                    <AppButton color="primary" onClick={handleExportSaft} disabled={isExportingSaft}>
                        {isExportingSaft && <Spinner size="sm" style={{marginRight: '8px'}} />}
                        {isExportingSaft ? t(TK.loading) : t(TK.exportSaft)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default Invoices;
